'use client'
import { useEffect, useState, useRef } from 'react'
import siteMetadata from '@/data/siteMetadata'
import headerNavLinks from '@/data/headerNavLinks'
import Link from './Link'
import MobileNav from './MobileNav'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { motion, AnimatePresence } from 'framer-motion'

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const pathname = usePathname()

  // Check if current page is privacy or imprint
  const isLegalPage =
    pathname === '/datenschutzrichtlinie' ||
    pathname === '/impressum' ||
    pathname === '/energyhouse' ||
    pathname === '/widerruf' ||
    pathname === '/bewerbung' ||
    pathname === '/teilnahmebedingungen' ||
    pathname === '/geschaftsbedingungen' ||
    pathname === '/cookie-einstellungen' ||
    pathname === '/hauseigentumer'

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleMouseEnter = (title: string) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current)
      dropdownTimeoutRef.current = null
    }
    setOpenDropdown(title)
  }

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setOpenDropdown(null)
    }, 300) // 300ms delay before closing
  }

  const headerClass = `
    fixed top-0 left-0 right-0 z-50
    flex items-center justify-between w-full py-4 px-4 md:px-0
    transition-all duration-300
    ${isScrolled || isLegalPage ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-transparent'}
  `

  // Product cards data for dropdown
  const productItems = [
    {
      title: 'Photovoltaik',
      href: '/photovoltaik',
      image: '/images/solar-panel.webp',
      description: 'Photovoltaikanlagen für optimale Stromerzeugung',
    },
    {
      title: 'Heizung',
      href: '/heizung',
      image: '/images/infrared-heater.webp',
      description: 'Infrarotheizungen - wirtschaftlich und effizient',
    },
    {
      title: 'Isolierung',
      href: '/isolierung',
      image: '/images/isolierung1.webp',
      description: 'Energiesparende Isolierung für Ihre Fassade',
    },
  ]

  // Wissensplattform items data for dropdown
  const wissensplattformItems = [
    {
      title: 'KI-Energiekonfigurator',
      href: '/energyhouse',
      image: '/images/konfigurator-page.png',
      description: 'Interaktives Tool zur Visualisierung von Energiemaßnahmen',
    },
    {
      title: 'Wiki',
      href: 'https://www.godeutschland.info',
      image: '/images/submenu-wiki.png',
      description: 'Umfangreiches Wissen rund um Energielösungen',
      openInNewTab: true,
    },
    {
      title: 'E-Auto Rechner',
      href: 'https://www.godeutschland.info/e-autorechner',
      image: '/images/submenu-e-auto.png',
      description: 'Berechnungen für Ihre Elektrofahrzeuge',
      openInNewTab: true,
    },
    {
      title: "Ben's Story",
      href: 'https://www.godeutschland.info/bensstory',
      image: '/images/submenu-ben-story.png',
      description: 'Erfahren Sie mehr über eine inspirierende Erfolgsgeschichte',
      openInNewTab: true,
    },
  ]

  // Über uns items data for dropdown
  const uberUnsItems = [
    {
      title: 'Wer sind wir?',
      href: '/uberuns',
      image: '/images/about-image.webp',
      description: 'Lernen Sie unser engagiertes Team kennen',
    },
    {
      title: 'Bewerbung',
      href: '/bewerbung',
      image: '/images/partners-image.jpg',
      description: 'Lernen Sie unser engagiertes Team kennen',
    },
    {
      title: 'Empfehlungsprogramm',
      href: '/empfehlungsprogramm',
      image: '/images/referral-horizontal.jpg',
      description: 'Sie und die empfohlene Person eine Prämie in Höhe von maximal 500 €',
    },
    {
      title: 'Partnerprogramm',
      href: '/partnerprogram',
      image: '/images/hero-partnerprogram.jpg',
      description: 'Empfehlen Sie GoDeutschland und sichern Sie Ihren Kunden Rabatte',
    },
  ]

  // Animation variants for dropdown menu
  const dropdownVariants = {
    hidden: {
      opacity: 0,
      y: -5,
      transition: {
        duration: 0.2,
        ease: 'easeInOut',
      },
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: 'easeOut',
      },
    },
    exit: {
      opacity: 0,
      y: -5,
      transition: {
        duration: 0.2,
        ease: 'easeInOut',
      },
    },
  }

  // Animation variants for product cards
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 },
    },
  }

  return (
    <header className={headerClass}>
      <div className="mx-auto flex w-full max-w-7xl items-center justify-between md:px-8 lg:px-0">
        <div className="flex items-center space-x-6">
          <Link href="/" aria-label={siteMetadata.headerTitle}>
            <div className="flex items-center">
              <div className="relative h-[50px] w-[200px]">
                <Image
                  src={
                    isScrolled || isLegalPage
                      ? '/images/logo-1-dark.png'
                      : '/images/logo-1-white.png'
                  }
                  alt={siteMetadata.headerTitle}
                  fill
                  priority
                  sizes="300px"
                  className="object-contain object-left transition-opacity duration-300"
                />
              </div>
            </div>
          </Link>
        </div>

        <div className="flex items-center justify-between pl-4 leading-5">
          <div className="hidden items-center space-x-4 xl:flex">
            {/* YouTube Link */}
            {headerNavLinks
              .filter((link) => link.isYoutube)
              .map((link) => (
                <Link
                  key={link.title}
                  href={link.href || '#'}
                  className={`hidden items-center space-x-2 font-medium sm:flex ${
                    isScrolled || isLegalPage
                      ? 'text-gray-900 hover:text-[#ff4500]'
                      : 'text-white hover:text-[#ff4500]'
                  }`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg
                    className={`h-5 w-5 ${
                      isScrolled || isLegalPage ? 'text-red-600' : 'text-current'
                    }`}
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                  </svg>
                  <span>{link.title}</span>
                </Link>
              ))}

            {headerNavLinks
              .filter((link) => !link.isYoutube && link.href !== '/')
              .map((link) => {
                if ('submenu' in link) {
                  return (
                    <div
                      key={link.title}
                      className="relative"
                      onMouseEnter={() => handleMouseEnter(link.title)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <Link
                        href={link.href || '#'}
                        className={`group flex items-center space-x-1 font-medium transition-colors ${
                          isScrolled || isLegalPage
                            ? openDropdown === link.title
                              ? 'text-[#ff4500]'
                              : 'text-gray-900 hover:text-[#ff4500]'
                            : openDropdown === link.title
                              ? 'text-[#ff4500]'
                              : 'text-white hover:text-[#ff4500]'
                        }`}
                      >
                        <span>{link.title}</span>
                        <span
                          className={`ml-1 inline-block transition-transform duration-200 ${
                            openDropdown === link.title ? 'rotate-180' : ''
                          }`}
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </span>
                      </Link>

                      <AnimatePresence>
                        {openDropdown === link.title &&
                          link.submenu &&
                          link.title === 'Volks-Lösungen' && (
                            <motion.div
                              variants={dropdownVariants}
                              initial="hidden"
                              animate="visible"
                              exit="exit"
                              className="fixed top-[90px] left-1/2 z-50 mt-2 w-[800px] -translate-x-1/2 transform rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800"
                              onMouseEnter={() => {
                                if (dropdownTimeoutRef.current) {
                                  clearTimeout(dropdownTimeoutRef.current)
                                  dropdownTimeoutRef.current = null
                                }
                              }}
                              onMouseLeave={handleMouseLeave}
                            >
                              <motion.div
                                className="grid grid-cols-3 gap-6"
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                              >
                                {productItems.map((product, index) => (
                                  <motion.div key={product.href} variants={itemVariants}>
                                    <Link
                                      href={product.href}
                                      className="group flex flex-col overflow-hidden rounded-lg bg-gray-50 transition-all duration-300 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600"
                                      onClick={handleMouseLeave} // Close dropdown on click
                                    >
                                      <div className="relative h-48 w-full overflow-hidden">
                                        <Image
                                          src={product.image}
                                          alt={product.title}
                                          fill
                                          className="object-cover transition-transform duration-300 group-hover:scale-105"
                                          sizes="(max-width: 768px) 100vw, 300px"
                                        />
                                      </div>
                                      <div className="p-4">
                                        <h3 className="mb-1 text-lg font-semibold text-gray-900 dark:text-white">
                                          {product.title}
                                        </h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-300">
                                          {product.description}
                                        </p>
                                      </div>
                                    </Link>
                                  </motion.div>
                                ))}
                              </motion.div>
                            </motion.div>
                          )}
                      </AnimatePresence>

                      <AnimatePresence>
                        {openDropdown === link.title &&
                          link.submenu &&
                          link.title === 'Wissensplattform' && (
                            <motion.div
                              variants={dropdownVariants}
                              initial="hidden"
                              animate="visible"
                              exit="exit"
                              className="fixed top-[90px] left-1/2 z-50 mt-2 w-[1200px] -translate-x-1/2 transform rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800"
                              onMouseEnter={() => {
                                if (dropdownTimeoutRef.current) {
                                  clearTimeout(dropdownTimeoutRef.current)
                                  dropdownTimeoutRef.current = null
                                }
                              }}
                              onMouseLeave={handleMouseLeave}
                            >
                              <motion.div
                                className="grid grid-cols-4 gap-6"
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                              >
                                {wissensplattformItems.map((item, index) => (
                                  <motion.div key={item.href} variants={itemVariants}>
                                    <Link
                                      href={item.href}
                                      className="group flex flex-col overflow-hidden rounded-lg bg-gray-50 transition-all duration-300 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600"
                                      target={item.openInNewTab ? '_blank' : undefined}
                                      rel={item.openInNewTab ? 'noopener noreferrer' : undefined}
                                      onClick={handleMouseLeave} // Close dropdown on click
                                    >
                                      <div className="relative h-48 w-full overflow-hidden bg-gray-200 dark:bg-gray-600">
                                        <Image
                                          src={item.image}
                                          alt={item.title}
                                          fill
                                          className="object-cover transition-transform duration-300 group-hover:scale-105"
                                          sizes="(max-width: 768px) 100vw, 300px"
                                        />
                                      </div>
                                      <div className="p-4">
                                        <h3 className="mb-1 text-lg font-semibold text-gray-900 dark:text-white">
                                          {item.title}
                                        </h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-300">
                                          {item.description}
                                        </p>
                                      </div>
                                    </Link>
                                  </motion.div>
                                ))}
                              </motion.div>
                            </motion.div>
                          )}
                      </AnimatePresence>

                      <AnimatePresence>
                        {openDropdown === link.title &&
                          link.submenu &&
                          link.title === 'Über uns' && (
                            <motion.div
                              variants={dropdownVariants}
                              initial="hidden"
                              animate="visible"
                              exit="exit"
                              className="fixed top-[90px] left-1/2 z-50 mt-2 w-[1200px] -translate-x-1/2 transform rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800"
                              onMouseEnter={() => {
                                if (dropdownTimeoutRef.current) {
                                  clearTimeout(dropdownTimeoutRef.current)
                                  dropdownTimeoutRef.current = null
                                }
                              }}
                              onMouseLeave={handleMouseLeave}
                            >
                              <motion.div
                                className="grid grid-cols-4 gap-6"
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                              >
                                {uberUnsItems.map((item, index) => (
                                  <motion.div key={item.href} variants={itemVariants}>
                                    <Link
                                      href={item.href}
                                      className="group flex flex-col overflow-hidden rounded-lg bg-gray-50 transition-all duration-300 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600"
                                      onClick={handleMouseLeave} // Close dropdown on click
                                    >
                                      <div className="relative h-48 w-full overflow-hidden bg-gray-200 dark:bg-gray-600">
                                        <Image
                                          src={item.image}
                                          alt={item.title}
                                          fill
                                          className="object-cover transition-transform duration-300 group-hover:scale-105"
                                          sizes="(max-width: 768px) 100vw, 300px"
                                        />
                                      </div>
                                      <div className="p-4">
                                        <h3 className="mb-1 text-lg font-semibold text-gray-900 dark:text-white">
                                          {item.title}
                                        </h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-300">
                                          {item.description}
                                        </p>
                                      </div>
                                    </Link>
                                  </motion.div>
                                ))}
                              </motion.div>
                            </motion.div>
                          )}
                      </AnimatePresence>

                      {/* <AnimatePresence>
                        {openDropdown === link.title &&
                          link.submenu &&
                          link.title !== 'Produkte' &&
                          link.title !== 'Wissensplattform' && (
                            <motion.div
                              variants={dropdownVariants}
                              initial="hidden"
                              animate="visible"
                              exit="exit"
                              className="absolute left-0 mt-2 w-48 rounded-md bg-white py-2 shadow-lg dark:bg-gray-800"
                              onMouseEnter={() => {
                                if (dropdownTimeoutRef.current) {
                                  clearTimeout(dropdownTimeoutRef.current)
                                  dropdownTimeoutRef.current = null
                                }
                              }}
                              onMouseLeave={handleMouseLeave}
                            >
                              {link.submenu.map((subItem) => (
                                <Link
                                  key={subItem.href}
                                  href={subItem.href}
                                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-[#ff4500] dark:text-gray-200 dark:hover:bg-gray-700"
                                  {...(subItem.openInNewTab && {
                                    target: '_blank',
                                    rel: 'noopener noreferrer',
                                  })}
                                >
                                  {subItem.title}
                                </Link>
                              ))}
                            </motion.div>
                          )}
                      </AnimatePresence> */}
                    </div>
                  )
                }
                return (
                  <Link
                    key={link.title}
                    href={link.href || '#'}
                    className={`${
                      link.isButton
                        ? 'rounded-lg bg-[#ff4500] px-4 py-2 font-semibold text-white transition-all hover:scale-105 hover:bg-[#e03e00] hover:text-white'
                        : link.isPhone
                          ? 'flex items-center space-x-2 font-medium transition-colors hover:text-[#ff4500]'
                          : 'font-medium transition-colors hover:text-[#ff4500]'
                    } ${isScrolled || isLegalPage ? 'text-gray-900' : 'text-white'}`}
                    {...(link.openInNewTab && { target: '_blank', rel: 'noopener noreferrer' })}
                  >
                    {link.isPhone && (
                      <svg
                        className="mr-2 h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                    )}
                    {link.title}
                  </Link>
                )
              })}
          </div>
          {/* <SearchButton /> */}
          <MobileNav />
        </div>
      </div>
    </header>
  )
}

export default Header
